import React from 'react'
import { Button, Select, Spinner, TextBox, Typography } from '@telavita-core/react-design-kit'

import ModalGeneric from '../ModalGeneric'

import { useCancelationSchedule } from './hooks/useCancelationSchedule'

import { 
  PATIENT_CANCELATION_SCHEDULE_REASONS_OPTIONS,
  SECRETARY_CANCELATION_SCHEDULE_REASONS_OPTIONS
} from 'settings/_cancelationScheduleReasonsOptions'

import { CancelationScheduleModalProps, RequesterProfile } from './types'

import * as S from './styles'

const SecretaryCancelationDetailMessage = (
  <Typography variant="content3" italic>
    Caso preenchido, profissional e paciente receberão por e-mail.
  </Typography>
)

const PatientCancelationDetailMessage = (
  <>
    <Typography variant="content3" italic>
      Caso preenchido, o profissional receberá por e-mail.
    </Typography>
    <Typography variant="content3" italic>
      Escreva de forma amigável, como se estivesse falando com ele.
    </Typography>
  </>
)

export const CancelationScheduleModal: React.FC<CancelationScheduleModalProps> = ({ 
  scheduleId, 
  requesterUsername,
  requesterProfile,
  onSuccessClose 
}) => {

  const { 
    cancelationFields, 
    handleCancelSchedule,
    handleChangeDetails,
    handleChangeReason,
    validateRequiredFields,
    isCancelationLoading,
    isCancelationSuccess,
    isCancelationError
  } = useCancelationSchedule({
    scheduleId,
    requesterUsername,
  })
  
  const feedbackOptions = {
    feedbackShow: isCancelationError || isCancelationSuccess,
    feedbackType: isCancelationError ? 'error' : 'success',
    feedbackMessage: isCancelationError ? 'Não foi possível cancelar a consulta' : 'Consulta cancelada \n com sucesso!',
    feedbackContent: isCancelationError ? 'Tente novamente em alguns instantes!' : '',
  }

  function getCancelationDetailMessage() {
    const profiles = {
      [RequesterProfile.SECRETARY]: SecretaryCancelationDetailMessage,
      [RequesterProfile.PATIENT]: PatientCancelationDetailMessage
    }

    return profiles[requesterProfile] || null
  }

  function getCancelationReasonsOptions() {
    const profiles = {
      [RequesterProfile.SECRETARY]: SECRETARY_CANCELATION_SCHEDULE_REASONS_OPTIONS,
      [RequesterProfile.PATIENT]: PATIENT_CANCELATION_SCHEDULE_REASONS_OPTIONS
    }

    return profiles[requesterProfile] || []
  }

  return (
    <ModalGeneric 
      modalTitle=""
      onSuccessClose={() => onSuccessClose()}
      {...feedbackOptions}
    >
      <S.Container>
        <S.HeaderWrapper>
          <Typography variant="header4">Cancelar consulta</Typography>
          <Typography variant="content2" color='danger'>Atenção, o cancelamento é irreversível.</Typography>
        </S.HeaderWrapper>

        <S.SelectWrapper>
          <Typography variant="content2" weight='bold'>
            Motivo do cancelamento*
          </Typography>
          <Select
            dataCy='select' 
            label=''
            onSelect={(data) => handleChangeReason(data)} 
            options={getCancelationReasonsOptions()} 
            selected={getCancelationReasonsOptions().find(option => option.code === cancelationFields.cancelationReason)}
            customClassName="customSelect"
          />
        </S.SelectWrapper>

        <S.TextBoxWrapper>
          <Typography variant="content2" weight='bold'>
            Detalhes do cancelamento
          </Typography>
          {getCancelationDetailMessage()}
          <TextBox 
            placeholder='Digite o motivo'
            name='cancelationDetails'
            value={cancelationFields.cancelationDetails}
            onChange={(event) => handleChangeDetails(event.target.value)}
          />
        </S.TextBoxWrapper>

        <S.CreditDisclaimer>
          <Typography variant="content3">
            <strong>No convênio</strong> o crédito será estornado e um novo agendamento poderá ser feito no prazo de validade.
          </Typography>
          <Typography variant="content3">
            <strong>No particular</strong> o valor será estornado integralmente até a próxima fatura do cartão de crédito.
          </Typography>
        </S.CreditDisclaimer>

        <Typography 
          variant="content2" 
          italic 
          inlineStyles={{ color: '#898989', margin: '20px 0' }}
        >
          * Preenchimento obrigatório
        </Typography>

        <Button
          customClassName='cancelationButton'
          variant="contained" 
          color="danger" 
          disabled={!validateRequiredFields() || isCancelationLoading}
          onClick={handleCancelSchedule}
        >
          {isCancelationLoading 
            ? <Spinner customClassName='cancelationButtonSpinner'/> 
            : 'Confirmar cancelamento' 
          }
        </Button>
      </S.Container>
    </ModalGeneric>
  )
}